import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 py-20" data-testid="reset-password-page">
        <div className="w-full max-w-sm text-center">
          <AlertCircle size={36} className="text-red-500 mx-auto mb-4" />
          <h2 className="font-lora text-navy text-xl font-normal mb-3">Invalid Reset Link</h2>
          <p className="font-serif text-navy/50 text-sm mb-6">This link is missing a reset token. Please request a new password reset.</p>
          <Link to="/forgot-password" className="text-navy font-serif text-sm underline underline-offset-2 hover:text-gold transition-colors">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 py-20" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/4bqnieq1_Company%20Logo.jpeg"
              alt="Logo"
              className="h-14 w-14 mx-auto mb-4 rounded-sm"
            />
          </Link>
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-2">De Nicola Consulting Co.</p>
          <h1 className="font-lora text-2xl text-navy font-normal">Choose New Password</h1>
          <div className="w-8 h-px bg-gold mx-auto mt-4"></div>
        </div>

        {success ? (
          <div className="text-center" data-testid="reset-success-message">
            <CheckCircle size={36} className="text-green-600 mx-auto mb-4" />
            <h2 className="font-lora text-navy text-lg font-normal mb-3">Password Updated</h2>
            <p className="font-serif text-navy/60 text-sm leading-relaxed mb-2">
              Your password has been changed successfully.
            </p>
            <p className="font-serif text-navy/40 text-xs">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">New Password</label>
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-soft-gray bg-white px-4 py-3 pr-12 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                placeholder="Minimum 8 characters"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-[38px] text-navy/40 hover:text-navy"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div>
              <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                placeholder="Re-enter your password"
                data-testid="reset-confirm-input"
              />
            </div>

            {error && <p className="text-red-600 font-serif text-sm" data-testid="reset-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-navy text-ivory py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
              data-testid="reset-submit-btn"
            >
              {loading ? <><Loader2 size={15} className="animate-spin" /> Updating...</> : "Update Password"}
            </button>

            <p className="text-center text-navy/50 font-serif text-sm mt-2">
              <Link to="/login" className="hover:text-navy transition-colors underline underline-offset-2">
                Back to Sign In
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
